/* eslint-disable react/no-unstable-nested-components */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";
import { TESTIDS } from "@/constants/testIds";
import { Plus, CheckCircle2, Trash2, Printer } from "lucide-react";
import { useAuth } from "@/lib/auth";
import Modal from "@/components/Modal";
import ColumnChooser from "@/components/ColumnChooser";
import { useColumnPrefs } from "@/hooks/useColumnPrefs";
import { useAppSettings } from "@/hooks/useAppSettings";

const STATUSES = ["Pending", "Scheduled", "Picked Up", "Cancelled"];

function statusBadge(s) {
  switch (s) {
    case "Pending": return "es-badge es-badge-warning";
    case "Scheduled": return "es-badge es-badge-info";
    case "Picked Up": return "es-badge es-badge-success";
    case "Cancelled": return "es-badge es-badge-error";
    default: return "es-badge es-badge-neutral";
  }
}

export default function DocketsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const settings = useAppSettings();
  const canCreate = ["admin", "user"].includes(user.role);
  const canConfirm = ["admin", "user", "warehouse"].includes(user.role);

  const orderedStatuses = useMemo(() => {
    const fromSettings = Array.isArray(settings?.Docket_Statuses) ? settings.Docket_Statuses : [];
    const allowed = new Set(STATUSES);
    const ordered = fromSettings.filter((s) => allowed.has(s));
    STATUSES.forEach((s) => { if (!ordered.includes(s)) ordered.push(s); });
    return ordered;
  }, [settings?.Docket_Statuses]);
  const labelFor = (s) => settings?.Docket_Status_Labels?.[s] || s;

  const [dockets, setDockets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [d, c, p] = await Promise.all([
        api.get("/dockets"), api.get("/companies"), api.get("/inventory"),
      ]);
      setDockets(d.data); setCompanies(c.data); setProducts(p.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const lookups = useMemo(() => ({
    company: Object.fromEntries(companies.map(c => [c.id, c])),
    product: Object.fromEntries(products.map(p => [p.id, p])),
  }), [companies, products]);

  const confirmPickup = async (id) => {
    try {
      await api.post(`/dockets/${id}/confirm-pickup`);
      setDockets(ds => ds.map(d => d.id === id ? { ...d, Docket_Status: "Picked Up" } : d));
      toast.success("Pickup confirmed.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this docket?")) return;
    try { await api.delete(`/dockets/${id}`); setDockets(ds => ds.filter(d => d.id !== id)); toast.success("Docket deleted."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const allColumns = [
    { key: "Docket_Number", label: "Docket", cell: (d) => <span className="font-mono font-bold text-sm">{d.Docket_Number}</span> },
    { key: "Company", label: "Company", cell: (d) => <span className="text-sm">{lookups.company[d.Company_ID]?.Company_Name || "—"}</span> },
    { key: "Product", label: "Product", cell: (d) => <span className="text-sm">{lookups.product[d.Product_ID]?.Product_Name || "—"}</span> },
    { key: "Quantity_To_Pick_Up", label: "Qty", align: "right", cell: (d) => <span className="font-mono font-bold">{d.Quantity_To_Pick_Up}</span> },
    { key: "Pickup_Deadline", label: "Pickup Deadline", cell: (d) => <span className="font-mono text-xs">{d.Pickup_Deadline}</span> },
    { key: "Delivery_Address", label: "Delivery", defaultVisible: false, cell: (d) => <span className="text-xs">{d.Delivery_Address || "—"}</span> },
    { key: "Internal_Notes", label: "Internal notes", defaultVisible: false, cell: (d) => <span className="text-xs text-zinc-500">{d.Internal_Notes || "—"}</span> },
    { key: "Docket_Status", label: "Status", cell: (d) => <span className={statusBadge(d.Docket_Status)}>{d.Docket_Status}</span> },
    {
      key: "_actions", label: "Actions", locked: true, align: "right",
      cell: (d) => (
        <div className="flex items-center justify-end gap-2">
          <button
            data-testid={`docket-print-${d.id}`}
            onClick={() => window.open(`/dockets/${d.id}/print`, "_blank")}
            className="text-zinc-500 hover:text-brand-700 p-1.5 rounded-md hover:bg-zinc-50"
            title="Print docket"
          >
            <Printer className="w-4 h-4" />
          </button>
          {canConfirm && d.Docket_Status !== "Picked Up" && d.Docket_Status !== "Cancelled" && (
            <button
              data-testid={TESTIDS.docketConfirmBtn(d.id)}
              onClick={() => confirmPickup(d.id)}
              className="es-btn-primary h-9"
            >
              <CheckCircle2 className="w-4 h-4" /> Confirm Pickup
            </button>
          )}
          {user.role === "admin" && (
            <button onClick={() => onDelete(d.id)} className="text-zinc-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
          )}
        </div>
      ),
    },
  ];

  const { visibleColumns, orderedColumns, isVisible, toggleColumn, moveColumn, reset } =
    useColumnPrefs("dockets", allColumns);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Dispatch Dockets</h1>
          <p className="text-sm text-zinc-500 mt-1">{dockets.length} docket{dockets.length === 1 ? "" : "s"} queued for the yard.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <ColumnChooser
            tableLabel="dockets"
            orderedColumns={orderedColumns}
            isVisible={isVisible}
            toggleColumn={toggleColumn}
            moveColumn={moveColumn}
            reset={reset}
          />
          {canCreate && (
            <button data-testid={TESTIDS.newDocketBtn} onClick={() => setShowNew(true)} className="es-btn-primary">
              <Plus className="w-4 h-4" /> New docket
            </button>
          )}
        </div>
      </div>

      {err && <div className="es-card p-4 text-sm text-red-700">{err}</div>}

      <div className="es-card overflow-x-auto">
        <table className="es-table">
          <thead>
            <tr>
              {visibleColumns.map((c) => (
                <th key={c.key} className={[c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "", c.width || ""].join(" ")}>
                  {c.header ? c.header() : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={visibleColumns.length} className="text-center py-10 font-mono text-xs text-zinc-500">Loading…</td></tr>}
            {!loading && dockets.length === 0 && <tr><td colSpan={visibleColumns.length} className="text-center py-10 font-mono text-xs text-zinc-500">No dockets yet.</td></tr>}
            {dockets.map((d) => (
              <tr key={d.id} data-testid={TESTIDS.docketRow(d.id)} className="text-base">
                {visibleColumns.map((c) => (
                  <td key={c.key} className={c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : ""}>{c.cell(d)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && (
        <NewDocketModal companies={companies} products={products}
          onClose={() => setShowNew(false)}
          onCreated={(d) => { setDockets(prev => [d, ...prev]); setShowNew(false); }} />
      )}
    </div>
  );
}

export function NewDocketModal({ companies, products, onClose, onCreated }) {
  const settings = useAppSettings();
  const leadDays = Number(settings?.Default_Pickup_Lead_Days ?? 3);
  const [form, setForm] = useState({
    Company_ID: "", Product_ID: "", Quantity_To_Pick_Up: 0,
    Pickup_Deadline: new Date(Date.now() + 86400000 * leadDays).toISOString().slice(0, 10),
    Delivery_Address: "", Docket_Status: "Pending", Internal_Notes: "", External_Notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      const payload = { ...form, Quantity_To_Pick_Up: Number(form.Quantity_To_Pick_Up) };
      const { data } = await api.post("/dockets", payload);
      onCreated(data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const u = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <Modal title="New Dispatch Docket" onClose={onClose} size="lg">
      <form onSubmit={onSubmit} className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="es-label">Company *</label>
            <select required value={form.Company_ID} onChange={e => u("Company_ID", e.target.value)} className="es-select w-full">
              <option value="">Select…</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.Company_Name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className="es-label">Product *</label>
            <select required value={form.Product_ID} onChange={e => u("Product_ID", e.target.value)} className="es-select w-full">
              <option value="">Select…</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.Product_Name}</option>)}
            </select>
          </div>
          <div>
            <label className="es-label">Quantity to pick up *</label>
            <input type="number" required min="1" value={form.Quantity_To_Pick_Up} onChange={e => u("Quantity_To_Pick_Up", e.target.value)} className="es-input" />
          </div>
          <div>
            <label className="es-label">Pickup deadline *</label>
            <input type="date" required value={form.Pickup_Deadline} onChange={e => u("Pickup_Deadline", e.target.value)} className="es-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="es-label">Delivery address</label>
            <input value={form.Delivery_Address} onChange={e => u("Delivery_Address", e.target.value)} className="es-input" />
          </div>
          <div>
            <label className="es-label">Status</label>
            <select value={form.Docket_Status} onChange={e => u("Docket_Status", e.target.value)} className="es-select w-full">
              {orderedStatuses.map(s => <option key={s} value={s}>{labelFor(s)}</option>)}
            </select>
          </div>

          {err && <div className="sm:col-span-2 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}

          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="es-btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="es-btn-primary">{saving ? "Saving…" : "Create docket"}</button>
          </div>
      </form>
    </Modal>
  );
}
